import { ApolloClient, NormalizedCacheObject } from "@apollo/client";

/**
 * Cache eviction utility for managing Apollo Client cache invalidation
 */

export interface EvictionConfig {
  /** Entity typename (e.g., "InvestorProfile") */
  typename: string;
  /** Entity ID to evict (optional - if not provided, evicts all) */
  id?: string;
  /** Related typenames to evict (e.g., lists, related entities) */
  relatedTypenames?: string[];
  /** Specific fields to evict on related types */
  fieldNames?: string[];
}

/**
 * Evict entity from cache by ID
 */
export function evictEntity(
  client: ApolloClient<NormalizedCacheObject>,
  typename: string,
  id: string
): void {
  const cacheId = client.cache.identify({ __typename: typename, id });

  if (cacheId) {
    client.cache.evict({ id: cacheId });
    client.cache.gc(); // Garbage collect orphaned references
  }
}

/**
 * Evict all instances of a typename (useful for lists)
 */
export function evictTypename(
  client: ApolloClient<NormalizedCacheObject>,
  typename: string
): void {
  client.cache.evict({ id: "ROOT_QUERY", fieldName: typename });
  client.cache.gc();
}

/**
 * Evict specific query fields (useful for paginated lists)
 */
export function evictQueryFields(
  client: ApolloClient<NormalizedCacheObject>,
  fieldNames: string[]
): void {
  fieldNames.forEach((fieldName) => {
    client.cache.evict({ id: "ROOT_QUERY", fieldName });
  });
  client.cache.gc();
}

/**
 * Comprehensive eviction for CRUD operations
 */
export function executeEviction(
  client: ApolloClient<NormalizedCacheObject>,
  config: EvictionConfig
): void {
  // Evict the specific entity if ID provided
  if (config.id) {
    evictEntity(client, config.typename, config.id);
  }

  // Evict related lists and queries
  if (config.relatedTypenames) {
    config.relatedTypenames.forEach((typename) => {
      evictTypename(client, typename);
    });
  }

  // Evict specific query fields
  if (config.fieldNames) {
    evictQueryFields(client, config.fieldNames);
  }

  // Always garbage collect
  client.cache.gc();
}

/**
 * TTL-based cache eviction
 * Returns true if cache entry is stale and should be refetched
 */
export function isCacheStale(
  lastFetchTime: number | undefined,
  ttlMs: number
): boolean {
  if (!lastFetchTime) {
    return true;
  }

  return Date.now() - lastFetchTime > ttlMs;
}

// TTL constant: 6 hours for all cached data
export const TTL = {
  CACHE_DURATION: 6 * 60 * 60 * 1000, // 6 hours in milliseconds
} as const;
